import React, { useMemo, useState } from "react";
import RegenerateInvoiceModal from "components/common/RegenerateInvoiceModal";
import CancelInvoiceModal from "components/common/CancelInvoiceModal";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { AmcContractRules } from "features/Amc/rules";
import { regenerateAmcInvoice } from "features/Amc/services/AmcService";
import { cancelInvoice } from "features/Invoices/services/InvoiceService";
import type { Amc } from "features/Amc/types/amc.types";
import {
  amcItemToBoqRow,
  boqRowToAmcPersistLine,
  type AmcBoqFormRow,
} from "features/Amc/utils/amcItems";
import { WorkOrderItemSource } from "features/WorkOrders/constants/work-order-item.constants";
import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import {
  invoiceHasMoneyMoved,
  useSourceActiveInvoice,
} from "features/Invoices/hooks/useSourceActiveInvoice";
import { InvoiceSourceType } from "features/Invoices/constants/invoice.constants";

function emptyAmcBoqRow(): AmcBoqFormRow {
  return {
    type: QuotationItemType.SERVICE,
    description: "",
    lineId: undefined,
    onHandQty: undefined,
    quantity: 1,
    unit: "",
    rate: 0,
    sourceType: WorkOrderItemSource.ADHOC,
    quotationItemId: null,
    originalQuantity: null,
  };
}

type Props = {
  contract: Amc;
  onSuccess: () => void | Promise<void>;
};

export default function AmcRegenerateInvoiceAction({
  contract,
  onSuccess,
}: Props) {
  const { can } = useAccess();
  const canRegenerate =
    can(Permissions.AMC.REGENERATE_INVOICE) &&
    AmcContractRules.canRegenerateInvoice(contract.status as any);
  const canCancelInvoice = can(Permissions.INVOICE.CANCEL);

  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const {
    activeInvoice,
    loading: activeInvoiceLoading,
    refetch: refetchActiveInvoice,
  } = useSourceActiveInvoice({
    sourceType: InvoiceSourceType.AMC,
    sourceId: contract.id,
    enabled: canRegenerate,
  });
  const moneyMoved = activeInvoice != null && invoiceHasMoneyMoved(activeInvoice);

  const contractItems = contract.items;
  const itemsEditor = useMemo(() => {
    if (contractItems == null) return undefined;
    const initialRows: AmcBoqFormRow[] = contractItems.length
      ? contractItems.map((item) => amcItemToBoqRow(item))
      : [emptyAmcBoqRow()];
    return {
      initialRows,
      getDefaultRow: emptyAmcBoqRow,
      toPayloadItems: (rows: AmcBoqFormRow[]) =>
        rows.map((row) => boqRowToAmcPersistLine(row)),
    };
  }, [contractItems]);

  const showRegenerateInvoice =
    canRegenerate &&
    !activeInvoiceLoading &&
    (activeInvoice == null || moneyMoved);
  const showCancelInvoice =
    canRegenerate &&
    !activeInvoiceLoading &&
    activeInvoice != null &&
    !moneyMoved &&
    canCancelInvoice;

  const runSuccess = async () => {
    await onSuccess();
    refetchActiveInvoice();
  };

  if (!showRegenerateInvoice && !showCancelInvoice) return null;

  return (
    <>
      {showRegenerateInvoice ? (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
          onClick={() => setRegenerateOpen(true)}
        >
          <i className="ri-refresh-line align-bottom" />
          Regenerate invoice
        </button>
      ) : null}

      {showCancelInvoice ? (
        <button
          type="button"
          className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
          onClick={() => setCancelOpen(true)}
          title="An active invoice already exists for this contract with nothing collected against it yet — cancel it before a new one can be generated."
        >
          <i className="ri-close-circle-line align-bottom" />
          Cancel invoice
        </button>
      ) : null}

      <RegenerateInvoiceModal
        show={regenerateOpen}
        onClose={() => setRegenerateOpen(false)}
        onSuccess={runSuccess}
        currentFinalAmount={contract.finalAmount}
        itemsEditor={itemsEditor}
        submit={(payload) => regenerateAmcInvoice(contract.id, payload)}
      />

      <CancelInvoiceModal
        show={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onSuccess={runSuccess}
        submit={(payload) => {
          if (!activeInvoice) throw new Error("No invoice selected.");
          return cancelInvoice(activeInvoice.id, payload);
        }}
      />
    </>
  );
}
